import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  BadgeCheck,
  BarChart3,
  Bell,
  Check,
  Loader2,
} from "lucide-react";
import Header from "../components/layout/Header";
import { studentService } from "../services/studentService";

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 300;

// Engagement-gap filters. `all` shows everyone; the rest narrow to a single
// adoption gap the admin can act on. Values mirror the API's `filter` enum.
const FILTERS = [
  { key: "all", labelKey: "engagement.filters.all", label: "All students" },
  { key: "yet_to_start", labelKey: "engagement.filters.yetToStart", label: "Yet to start" },
  { key: "no_resume", labelKey: "engagement.filters.noResume", label: "No resume" },
  { key: "no_hypepage", labelKey: "engagement.filters.noHypepage", label: "No hypepage" },
  { key: "not_verified", labelKey: "engagement.filters.notVerified", label: "Not verified" },
];

const formatDate = (value, locale) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(locale, { month: "short", day: "numeric", year: "numeric" });
};

const fullName = (s) => `${s.firstName || ""} ${s.lastName || ""}`.trim();

// Clickable, sortable column header. `sortKey` null = not sortable.
const SortHeader = ({ label, sortKey, sortBy, sortDir, onSort, align }) => {
  if (!sortKey) {
    return <th className={`px-6 py-3 ${align === "center" ? "text-center" : ""}`}>{label}</th>;
  }
  const isActive = sortBy === sortKey;
  return (
    <th className={`px-6 py-3 ${align === "center" ? "text-center" : ""}`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        aria-sort={isActive ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
        className={`group inline-flex items-center gap-1 hover:text-primary transition-colors ${isActive ? "text-primary" : ""} ${
          align === "center" ? "justify-center" : ""
        }`}
      >
        {label}
        {isActive ? (
          sortDir === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />
        ) : (
          <ChevronsUpDown size={12} className="text-gray-300 group-hover:text-primary/60" />
        )}
      </button>
    </th>
  );
};

// ✓ / — cell for the boolean engagement flags (resume, hypepage).
const BoolCell = ({ value }) =>
  value ? (
    <BadgeCheck className="inline-block w-4 h-4 text-emerald-600" />
  ) : (
    <span className="text-gray-300">—</span>
  );

// `embedded` renders just the panel (filters + table) without the page chrome
// (top Header + min-h-screen wrapper + page title), for hosting inside the
// Manage Students "Engagement" tab. Standalone route passes embedded=false.
const Engagement = ({ embedded = false } = {}) => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initial filter comes from the URL (?filter=…) so the dashboard cards can
  // deep-link straight into a pre-filtered view.
  const initialFilter = FILTERS.some((f) => f.key === searchParams.get("filter"))
    ? searchParams.get("filter")
    : "all";

  const [filter, setFilter] = useState(initialFilter);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [schoolName, setSchoolName] = useState("");
  const [adminName, setAdminName] = useState("");
  // Per-row in-flight reminders and ids already reminded this session (so the
  // button disables to a "Reminded" state and we don't double-nudge).
  const [remindingIds, setRemindingIds] = useState(new Set());
  const [remindedIds, setRemindedIds] = useState(new Set());
  const [bulkReminding, setBulkReminding] = useState(false);

  // Header school/admin name (same source the dashboard uses).
  useEffect(() => {
    studentService
      .getDashboardStats()
      .then((s) => {
        setSchoolName(s.schoolName || "");
        setAdminName([s.adminFirstName, s.adminLastName].filter(Boolean).join(" "));
      })
      .catch(() => undefined);
  }, []);

  // Debounce the search input into the request trigger.
  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [searchInput]);

  // Reset to page 1 whenever the filter/sort changes.
  useEffect(() => {
    setPage(1);
  }, [filter, sortBy, sortDir]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await studentService.getStudentEngagement({
        page,
        limit: PAGE_SIZE,
        search,
        filter,
        sortBy,
        sortDir,
      });
      setRows(res.data);
      setTotal(res.meta.total);
      setTotalPages(res.meta.totalPages);
    } catch (error) {
      toast.error(error.message || t("engagement.loadFailed", { defaultValue: "Failed to load engagement" }));
      setRows([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [page, search, filter, sortBy, sortDir, t]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      // Counts read best high-to-low; name/date default the natural way.
      setSortDir(key === "name" ? "asc" : "desc");
    }
  };

  // Single-student nudge. Only offered for "yet to start" students, since the
  // reminder email is the "add your first achievement" copy.
  const handleRemind = async (student) => {
    setRemindingIds((prev) => new Set(prev).add(student.id));
    try {
      await studentService.remindStudents([student.id]);
      setRemindedIds((prev) => new Set(prev).add(student.id));
      toast.success(
        student.invited
          ? t("engagement.inviteResentToast", { defaultValue: `Invite re-sent to ${student.email}`, email: student.email })
          : t("engagement.remindSent", { defaultValue: `Reminder sent to ${student.email}`, email: student.email }),
      );
    } catch (error) {
      toast.error(error.message || t("engagement.remindFailed", { defaultValue: "Failed to send reminder" }));
    } finally {
      setRemindingIds((prev) => {
        const next = new Set(prev);
        next.delete(student.id);
        return next;
      });
    }
  };

  // Bulk nudge — reminds every "yet to start" student (across all pages, not
  // just the visible one), so we fetch the full set of matching ids first.
  const handleRemindAllYetToStart = async () => {
    setBulkReminding(true);
    try {
      const res = await studentService.getStudentEngagement({
        page: 1,
        limit: 500,
        filter: "yet_to_start",
      });
      const ids = (res.data || []).map((s) => s.id);
      if (ids.length === 0) {
        toast.info(t("engagement.remindNone", { defaultValue: "No students to remind." }));
        return;
      }
      if (!window.confirm(t("engagement.remindAllConfirm", { defaultValue: `Remind ${ids.length} student(s) who haven't started? Joined students get an "add your first achievement" nudge; pending invites are re-sent.`, count: ids.length }))) {
        return;
      }
      const result = await studentService.remindStudents(ids);
      setRemindedIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.add(id));
        return next;
      });
      toast.success(
        t("engagement.remindBulkSent", {
          defaultValue: `Sent ${result.sent} reminder(s).`,
          count: result.sent,
        }),
      );
    } catch (error) {
      toast.error(error.message || t("engagement.remindFailed", { defaultValue: "Failed to send reminders" }));
    } finally {
      setBulkReminding(false);
    }
  };

  const handleFilterChange = (key) => {
    setFilter(key);
    // Keep the URL in sync so the view is shareable/bookmarkable.
    if (key === "all") {
      searchParams.delete("filter");
    } else {
      searchParams.set("filter", key);
    }
    setSearchParams(searchParams, { replace: true });
  };

  const content = (
    <div className={embedded ? "flex flex-col gap-6" : "ml-[23px] mt-6 px-8 flex flex-col gap-6"}>
        {!embedded && (
          <div className="flex flex-col gap-0.5 font-['Manrope']">
            <h2 className="text-gray-900 text-2xl font-bold">
              {t("engagement.title", { defaultValue: "Student engagement" })}
            </h2>
            <p className="text-gray-500 text-base font-normal">
              {t("engagement.subtitle", {
                defaultValue: "See how each student is using Hypebook, and spot who needs a nudge.",
              })}
            </p>
          </div>
        )}

        {/* Filter chips */}
        <div className="flex flex-wrap items-center gap-2">
          {FILTERS.map((f) => {
            const active = filter === f.key;
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => handleFilterChange(f.key)}
                className={`h-9 px-4 rounded-full text-sm font-semibold border transition-colors ${
                  active
                    ? "bg-primary text-white border-primary"
                    : "bg-white text-gray-700 border-blue-100 hover:bg-blue-50"
                }`}
              >
                {t(f.labelKey, { defaultValue: f.label })}
              </button>
            );
          })}
        </div>

        {/* Search + bulk action */}
        <div className="flex justify-between items-center gap-4 flex-wrap">
          <div className="w-96 h-12 bg-white rounded-lg outline outline-1 outline-blue-100 flex overflow-hidden">
            <input
              type="text"
              placeholder={t("manageStudents.searchPlaceholder", { defaultValue: "Search by email or name..." })}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="flex-1 px-4 py-3 text-gray-500 font-medium outline-none"
            />
            <div className="px-6 bg-secondary border-l border-blue-100 flex items-center text-gray-500">
              <Search size={18} />
            </div>
          </div>

          {/* Bulk nudge — only when viewing the "yet to start" group and there
              are students to nudge. */}
          {filter === "yet_to_start" && total > 0 && (
            <button
              type="button"
              onClick={handleRemindAllYetToStart}
              disabled={bulkReminding}
              className="h-11 px-4 bg-primary hover:bg-blue-600 text-white text-base font-medium rounded-lg flex items-center gap-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {bulkReminding ? <Loader2 size={18} className="animate-spin" /> : <Bell size={18} />}
              {bulkReminding
                ? t("engagement.sending", { defaultValue: "Sending…" })
                : t("engagement.remindAll", { defaultValue: "Remind all yet to start" })}
            </button>
          )}
        </div>

        {/* Table */}
        {loading ? (
          <div className="bg-white rounded-xl outline outline-1 outline-blue-100 p-12 flex justify-center items-center">
            <div className="text-gray-500 text-lg">{t("manageStudents.loading", { defaultValue: "Loading..." })}</div>
          </div>
        ) : (
          <div className="bg-white rounded-xl outline outline-1 outline-blue-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-secondary">
                  <tr className="text-gray-900 text-xs font-semibold">
                    <SortHeader label={t("engagement.columns.student", { defaultValue: "Student" })} sortKey="name" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                    <SortHeader label={t("engagement.columns.grade", { defaultValue: "Grade" })} sortKey={null} />
                    <SortHeader label={t("engagement.columns.gradYear", { defaultValue: "Grad year" })} sortKey={null} />
                    <SortHeader label={t("engagement.columns.achievements", { defaultValue: "Achievements" })} sortKey="achievements" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="center" />
                    <SortHeader label={t("engagement.columns.verified", { defaultValue: "Verified" })} sortKey="verified" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="center" />
                    <SortHeader label={t("engagement.columns.resume", { defaultValue: "Resume" })} sortKey={null} align="center" />
                    <SortHeader label={t("engagement.columns.hypepage", { defaultValue: "Hypepage" })} sortKey={null} align="center" />
                    <SortHeader label={t("engagement.columns.hypes", { defaultValue: "Hypes" })} sortKey="hypes" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="center" />
                    <SortHeader label={t("engagement.columns.lastActive", { defaultValue: "Last active" })} sortKey="lastActive" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                    <SortHeader label={t("engagement.columns.action", { defaultValue: "Action" })} sortKey={null} align="center" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-blue-100">
                  {rows.length > 0 ? (
                    rows.map((s) => (
                      <tr key={s.id} className="text-gray-900 text-sm hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="font-medium flex items-center gap-2">
                            <span>{fullName(s) || t("manageStudents.na", { defaultValue: "—" })}</span>
                            {s.invited ? (
                              <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                                {t("engagement.invitedBadge", { defaultValue: "Invited" })}
                              </span>
                            ) : null}
                          </div>
                          <div className="text-gray-400 text-xs">{s.email}</div>
                        </td>
                        <td className="px-6 py-4 text-gray-700">{s.grade || "—"}</td>
                        <td className="px-6 py-4 text-gray-700">
                          {s.expectedGraduationDate ? new Date(s.expectedGraduationDate).getFullYear() : "—"}
                        </td>
                        <td className="px-6 py-4 text-center">{s.achievementsCount}</td>
                        <td className="px-6 py-4 text-center">{s.verifiedCount}</td>
                        <td className="px-6 py-4 text-center"><BoolCell value={s.hasResume} /></td>
                        <td className="px-6 py-4 text-center"><BoolCell value={s.hasHypepage} /></td>
                        <td className="px-6 py-4 text-center">{s.hypesReceived}</td>
                        <td className="px-6 py-4 text-gray-500">
                          {s.invited ? <span className="text-gray-300">—</span> : formatDate(s.lastActiveAt, i18n.resolvedLanguage || "en")}
                        </td>
                        <td className="px-6 py-4 text-center">
                          {s.invited || s.achievementsCount === 0 ? (
                            remindedIds.has(s.id) ? (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
                                <Check size={14} />{" "}
                                {s.invited
                                  ? t("engagement.inviteSent", { defaultValue: "Invite sent" })
                                  : t("engagement.reminded", { defaultValue: "Reminded" })}
                              </span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => handleRemind(s)}
                                disabled={remindingIds.has(s.id)}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-blue-50 text-primary text-xs font-semibold hover:bg-blue-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                              >
                                {remindingIds.has(s.id) ? <Loader2 size={13} className="animate-spin" /> : <Bell size={13} />}
                                {s.invited
                                  ? t("engagement.resendInvite", { defaultValue: "Resend invite" })
                                  : t("engagement.remind", { defaultValue: "Remind" })}
                              </button>
                            )
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="10" className="px-6 py-12 text-center text-gray-500">
                        <BarChart3 className="inline-block w-6 h-6 text-gray-300 mb-2" />
                        <div className="text-sm">
                          {t("engagement.empty", { defaultValue: "No students match this view." })}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        <div className="flex justify-between items-center font-['Manrope']">
          <span className="text-sm text-gray-500">
            {total === 0
              ? t("engagement.noResults", { defaultValue: "No results" })
              : t("engagement.showing", {
                  defaultValue: `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} of ${total}`,
                  from: (page - 1) * PAGE_SIZE + 1,
                  to: Math.min(page * PAGE_SIZE, total),
                  total,
                })}
          </span>
          <div className="flex items-center gap-2 bg-white rounded-full outline outline-1 outline-blue-100 p-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
              className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label={t("engagement.prevPage", { defaultValue: "Previous page" })}
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm text-gray-700 px-2">
              {t("engagement.pageOf", { defaultValue: `Page ${page} of ${totalPages}`, page, totalPages })}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
              className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label={t("engagement.nextPage", { defaultValue: "Next page" })}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
  );

  return embedded ? (
    content
  ) : (
    <div className="bg-white min-h-screen font-manrope pb-[100px]">
      <Header schoolName={schoolName} adminName={adminName} />
      {content}
    </div>
  );
};

export default Engagement;
